import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Tones map to the fixed status roles, never to categorical series colours — a status
 * colour must not read as "series 4". The tint, the border and the ink of each tone are
 * resolved into `--tint-*` tokens in `globals.css` rather than mixed here; the note
 * there says why, and it is the reason a renewal badge can be read at all on an older
 * browser.
 *
 * The chips are deliberately quiet. A policy row can carry three of them at once, and a
 * table where every row shouts in saturated pills reads as an alarm panel rather than a
 * book of business. So: a ~9% wash of the tone, a border at ~18% of the same tone
 * (present, but never a drawn outline), and ink that is the tone pulled deep toward the
 * brand ink rather than the raw accent. Mixing toward the navy ink instead of black is
 * what keeps a muted chip in the Orion family instead of turning it grey-brown.
 *
 * Saturation is spent on the one thing that must interrupt — `critical` — and nowhere
 * else.
 */
const badgeVariants = cva(
  [
    'inline-flex w-fit shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border',
    'px-2 py-0.5 text-xs font-medium tracking-[-0.005em] transition-colors duration-150',
    '[&>svg]:pointer-events-none [&>svg]:size-3 [&>svg]:opacity-80',
  ].join(' '),
  {
    variants: {
      tone: {
        neutral: 'border-transparent bg-[var(--tint-neutral-bg)] text-muted-foreground',
        info: 'border-[var(--tint-info-border)] bg-[var(--tint-info-bg)] text-[var(--tint-info-fg)]',
        good: 'border-[var(--tint-good-border)] bg-[var(--tint-good-bg)] text-[var(--tint-good-fg)]',
        warning:
          'border-[var(--tint-warning-border)] bg-[var(--tint-warning-bg)] text-[var(--tint-warning-fg)]',
        serious:
          'border-[var(--tint-serious-border)] bg-[var(--tint-serious-bg)] text-[var(--tint-serious-fg)]',
        // The one tone allowed to stay legible-loud: an expired policy must interrupt.
        critical:
          'border-[var(--tint-critical-border)] bg-[var(--tint-critical-bg)] text-[var(--tint-critical-fg)]',
        outline: 'border-border-strong bg-transparent text-muted-foreground',
      },
      size: {
        default: '',
        sm: 'px-1.5 py-px text-[11px]',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'default' },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

export interface BadgeProps
  extends React.ComponentProps<'span'>,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

function Badge({ className, tone, size, asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot : 'span';
  return <Comp data-slot="badge" className={cn(badgeVariants({ tone, size }), className)} {...props} />;
}

/** A 6px dot in a status tone — for dense rows where a full badge is too heavy. */
function StatusDot({ tone = 'neutral', className }: { tone?: BadgeTone; className?: string }) {
  const toneClass: Record<string, string> = {
    neutral: 'bg-neutral',
    info: 'bg-info',
    good: 'bg-good',
    warning: 'bg-warning',
    serious: 'bg-serious',
    critical: 'bg-critical',
    outline: 'bg-border',
  };
  return (
    <span
      aria-hidden
      className={cn('inline-block size-1.5 rounded-full shrink-0', toneClass[tone], className)}
    />
  );
}

export { Badge, badgeVariants, StatusDot };
